const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/Medication/Medication.tsx';
import { useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { IAdministrationPoint } from './AdministrationPoint/AdministrationPoint';
import { AdministrationPointList } from './AdministrationPoint/AdministrationPointList';
import { MedicationLayout } from './MedicationLayout';
import { MedicationTag } from '@cistec/common_web/components/MedicationTag/MedicationTag';
import ProgressiveImage from '@cistec/common_web/components/ProgressiveImage/ProgressiveImage';
import { getMedicationImageUrl, ImageContent } from '@cistec/common_web/utils/medicationUtils';
import { css, cx } from '@emotion/css';
import { useIntl } from 'react-intl';
import textMap from './translations';
import { useTheme } from '@mui/material/styles';

export interface IMedication {
    /**
     * NAME
     */
    title: string;
    administrationPoints: IAdministrationPoint[];
    paidByPatient: boolean;
    /**
     * FKEY of the medication
     */
    fkey?: string;
}

/**
 * React props
 */

type IMedicationProps = IMedication;

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            card: css`
                max-width: 100%;
            `,
            cardHeader: css`
                line-height: 1.4;
            `,
            mediaContainer: css`
                align-self: stretch;
                padding-top: ${theme.spacing(0.5)};
                min-height: ${theme.spacing(134 / 8)};
                display: flex;
            `,
            noImage: css`
                display: none;
            `,
            noImgBox: css`
                display: flex;
                flex: 1;
                justify-content: center;
                align-items: center;
                background: ${theme.palette.grey[50]};
                color: ${theme.palette.grey[400]};
            `,
            flexBoxColumn: css`
                display: flex;
                flex-direction: column;
            `,
        }),
        [theme]
    );
}

/**
 * Functional React component
 */
export const Medication = (props: IMedicationProps) => {
    const classes = useCSSClasses();
    const { formatMessage } = useIntl();
    const [hasImage, setHasImage] = useState(true);

    const { title, fkey, administrationPoints, paidByPatient } = props;

    const header = (
        <Typography variant="h5" component="h2" className={classes.cardHeader}>
            {title}
        </Typography>
    );

    const imageUrlFrontSmall = getMedicationImageUrl(fkey || '', ImageContent.PackageFront, 'T');
    const imageUrlFront = getMedicationImageUrl(fkey || '', ImageContent.PackageFront);

    const content = (
        <div className={classes.flexBoxColumn}>
            <div>
                <div className={cx([classes.mediaContainer, (!hasImage || !fkey) && classes.noImage])}>
                    {imageUrlFront ? (
                        <ProgressiveImage
                            largeUrl={imageUrlFront}
                            smallUrl={imageUrlFrontSmall}
                            onImageLoaded={setHasImage}
                        />
                    ) : (
                        <div className={classes.noImgBox}>
                            <Typography variant="h3">{formatMessage(textMap.noImage)}</Typography>
                        </div>
                    )}
                </div>
                <MedicationTag ownMedication={paidByPatient} />
            </div>
        </div>
    );

    return (
        <MedicationLayout
            header={header}
            content={content}
            appointmentList={<AdministrationPointList administrationPoints={administrationPoints} />}
        />
    );
};
